import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Code, ShoppingCart, Smartphone, CheckCircle, Star, Quote, Sparkles } from 'lucide-react';
import ContactForm from '../components/ContactForm';
import ParticleBackground from '../components/ParticleBackground';
import AnimatedGradient from '../components/AnimatedGradient';

const Home = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    // Intersection Observer for fade-in animations
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.fade-in');
    elements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) {
        elements.forEach((el) => observerRef.current.unobserve(el));
      }
    };
  }, []);

  const services = [
    {
      icon: <Code size={40} />,
      title: 'Web Development',
      description: 'Custom websites built with modern technologies for optimal performance and user experience.',
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      icon: <ShoppingCart size={40} />,
      title: 'E-commerce',
      description: 'Full-featured online stores with secure payment processing and inventory management.',
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      icon: <Smartphone size={40} />,
      title: 'Custom Apps',
      description: 'Tailored applications that solve your unique business challenges and streamline operations.',
      gradient: 'from-green-500 to-emerald-500',
    },
  ];

  const portfolioItems = [
    {
      id: 1,
      title: 'Fintech Dashboard',
      category: 'Dashboard',
      gradient: 'from-blue-600 to-indigo-600',
    },
    {
      id: 2,
      title: 'E-commerce Store',
      category: 'E-commerce',
      gradient: 'from-purple-600 to-pink-600',
    },
    {
      id: 3,
      title: 'Admin Dashboard',
      category: 'Dashboard',
      gradient: 'from-green-600 to-teal-600',
    },
    {
      id: 4,
      title: 'Job Board',
      category: 'Platform',
      gradient: 'from-orange-600 to-red-600',
    },
    {
      id: 5,
      title: 'Modern Landing Page',
      category: 'Marketing',
      gradient: 'from-cyan-600 to-blue-600',
    },
    {
      id: 6,
      title: 'Social Media Dashboard',
      category: 'Dashboard',
      gradient: 'from-violet-600 to-purple-600',
    },
  ];

  const whyChoose = [
    'Expert team with 10+ years of combined experience',
    'Modern tech stack for scalable solutions',
    'Dedicated support and maintenance',
    'Proven track record of successful projects',
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'CEO, TechStart Inc.',
      content: 'BluePeak transformed our online presence. Their attention to detail and technical expertise is unmatched.',
      rating: 5,
    },
    {
      name: 'Michael Chen',
      role: 'Founder, RetailPro',
      content: 'Working with BluePeak was seamless. They delivered our e-commerce platform on time and exceeded expectations.',
      rating: 5,
    },
    {
      name: 'Emily Rodriguez',
      role: 'Director, FinanceHub',
      content: 'The dashboard they built for us is intuitive and powerful. Our team productivity has increased significantly.',
      rating: 5,
    },
  ];

  return (
    <div className="pt-16 overflow-hidden">
      {/* Hero Section with Particles */}
      <section className="relative bg-gradient-to-br from-primary-600 via-purple-600 to-pink-600 text-white py-20 min-h-[90vh] flex items-center overflow-hidden">
        <ParticleBackground />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(139,92,246,0.2),transparent_50%)]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6 animate-float">
              <Sparkles size={16} className="text-yellow-300 animate-sparkle" />
              <span className="text-sm font-semibold">Cutting-Edge Technology</span>
              <Sparkles size={16} className="text-yellow-300 animate-sparkle" style={{ animationDelay: '1s' }} />
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-slide-up">
              Digital Solutions That
              <br />
              <span className="bg-gradient-to-r from-yellow-300 via-pink-300 to-purple-300 bg-clip-text text-transparent animate-glow">
                Drive Success
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto animate-slide-up" style={{ animationDelay: '0.2s' }}>
              We build exceptional web applications, e-commerce platforms, and custom solutions
              that help businesses thrive in the digital world.
            </p>
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-primary-50 transition-all duration-300 shadow-2xl hover:shadow-primary-500/50 hover:scale-105 animate-slide-up tech-glow"
              style={{ animationDelay: '0.4s' }}
            >
              Get Started
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(59,130,246,0.02)_50%,transparent_75%)] bg-[length:20px_20px]"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 fade-in">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our <span className="text-primary-600">Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive digital solutions tailored to your business needs
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="group fade-in bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 tech-glow border border-gray-100"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`relative mb-4 inline-block p-4 rounded-xl bg-gradient-to-br ${service.gradient} transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                  <div className="text-white relative z-10">{service.icon}</div>
                  <div className="absolute inset-0 bg-white/20 rounded-xl blur-xl group-hover:blur-2xl transition-all"></div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Preview */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-in">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Featured <span className="text-primary-600">Projects</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Explore our portfolio of successful digital projects
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {portfolioItems.slice(0, 6).map((item, index) => (
              <div
                key={item.id}
                className="group fade-in bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 card-3d"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`relative h-48 bg-gradient-to-br ${item.gradient} flex items-center justify-center overflow-hidden`}>
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.2),transparent)]"></div>
                  <span className="text-white text-lg font-semibold relative z-10 group-hover:scale-110 transition-transform duration-300">
                    {item.title}
                  </span>
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute top-2 right-2 w-2 h-2 bg-yellow-300 rounded-full animate-pulse"></div>
                    <div className="absolute bottom-4 left-4 w-1 h-1 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                    <div className="absolute top-1/2 right-4 w-1.5 h-1.5 bg-cyan-300 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
                  </div>
                </div>
                <div className="p-6">
                  <span className="text-sm text-primary-600 font-medium bg-primary-50 px-3 py-1 rounded-full">
                    {item.category}
                  </span>
                  <h3 className="text-xl font-bold text-gray-900 mt-3 mb-4 group-hover:text-primary-600 transition-colors">
                    {item.title}
                  </h3>
                  <Link
                    to="/portfolio"
                    className="text-primary-600 font-semibold hover:text-primary-700 flex items-center gap-2 group-hover:gap-3 transition-all"
                  >
                    View Project
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center fade-in">
            <Link
              to="/portfolio"
              className="group inline-flex items-center gap-2 bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 tech-glow"
            >
              View All Projects
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary-400 rounded-full mix-blend-multiply filter blur-3xl animate-float"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 fade-in">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose <span className="text-primary-600">BluePeak</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We're committed to delivering excellence in every project
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {whyChoose.map((point, index) => (
              <div
                key={index}
                className="group fade-in flex items-start gap-4 bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex-shrink-0">
                  <CheckCircle className="text-primary-600 group-hover:scale-125 transition-transform duration-300" size={24} />
                </div>
                <p className="text-gray-700 text-lg group-hover:text-gray-900 transition-colors">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-in">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What Our <span className="text-primary-600">Clients Say</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Don't just take our word for it
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="group fade-in bg-white p-8 rounded-xl shadow-lg border-l-4 border-primary-600 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 relative overflow-hidden"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary-100 rounded-full -mr-16 -mt-16 opacity-20 group-hover:opacity-30 transition-opacity"></div>
                <div className="relative z-10">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star
                        key={i}
                        className="text-yellow-400 fill-current group-hover:scale-110 transition-transform"
                        size={20}
                        style={{ transitionDelay: `${i * 0.05}s` }}
                      />
                    ))}
                  </div>
                  <Quote className="text-primary-600 mb-4 group-hover:scale-110 transition-transform" size={32} />
                  <p className="text-gray-700 mb-6 italic leading-relaxed">"{testimonial.content}"</p>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-gray-600 text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(135deg,transparent_25%,rgba(59,130,246,0.03)_50%,transparent_75%)] bg-[length:30px_30px]"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12 fade-in">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Get In <span className="text-primary-600">Touch</span>
            </h2>
            <p className="text-xl text-gray-600">
              Ready to start your project? Let's talk!
            </p>
          </div>
          <div className="fade-in bg-white p-8 md:p-12 rounded-xl shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
